import { ReactNode, Suspense, useEffect } from 'react'
import { AspectRatio, Image, Loader, Stack } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import clsx from 'clsx'
import Meta from '../common/Meta'
import { Typography } from '../common/typography'
import { useCompetitionId } from '../common/hooks'
import { useCompetition } from '../common/components/header/api'
import { formatDateString } from '../common/helpers'
import { ReactComponent as LeaguestarLogo } from '../assets/leaguestar_logo.svg'
import { initRootScaler } from './scale'
import classes from './Dashboard.module.css'

interface Props {
  /** Number of slides to show indicators for */
  slideCount: number
  /** Currently active slide index */
  currentSlide: number
  /** Progress (0-100) within the active slide for the rotation indicator */
  slideProgress: number
  /** Slide content (rotated externally; remounted on key change for fade animation) */
  children: ReactNode
  /** Optional per-slide title shown centered in the header */
  slideTitle?: string
  /**
   * When true (default), apply `initRootScaler` so the chrome auto-scales to
   * the viewport. Set false when the chrome is rendered inside another scaled
   * canvas (e.g. the dashboard editor's preview) — the parent owns the scale.
   */
  autoScale?: boolean
}

/**
 * The fixed 1920x1080 chrome around dashboard content.
 * Handles the platform/competition logos, footer with "Updated" timestamp,
 * slide indicators, LeagueStar branding, and root scaling for the viewport.
 */
export function DashboardChrome({
  slideCount,
  currentSlide,
  slideProgress,
  children,
  slideTitle,
  autoScale = true,
}: Props) {
  const competitionId = useCompetitionId()

  useEffect(() => {
    if (!autoScale) return
    const cleanup = initRootScaler('dashboard')
    return cleanup
  }, [autoScale])

  return (
    <div className={classes.wrap}>
      <div
        id={autoScale ? 'dashboard' : undefined}
        className={classes.container}
        style={
          autoScale
            ? undefined
            : { position: 'relative', top: 'auto', left: 'auto' }
        }
      >
        <Suspense fallback={null}>
          <ChromeHeader competitionId={competitionId} slideTitle={slideTitle} />
        </Suspense>
        <div className={classes.background} />
        <div className={classes.content} key={currentSlide}>
          <Suspense
            fallback={
              <Stack w="100%" h="100%" justify="center" align="center">
                <Loader color="#0B6B7B" />
              </Stack>
            }
          >
            {children}
          </Suspense>
        </div>
        <div className={classes.footer}>
          <Suspense fallback={<div />}>
            <ChromeFooterUpdated competitionId={competitionId} />
          </Suspense>
          <div className={classes.indicatorsWrapper}>
            <div className={classes.indicators}>
              {Array.from({ length: slideCount }).map((_, index) => (
                <div
                  key={index}
                  className={clsx(classes.indicator, {
                    [classes.indicatorActive]: index === currentSlide,
                  })}
                >
                  {index === currentSlide && (
                    <div
                      className={classes.indicatorProgress}
                      style={{ width: `${slideProgress}%` }}
                    />
                  )}
                </div>
              ))}
            </div>
            <Typography
              fs="lg"
              fw={400}
              fc={{ light: 'gray-3', dark: 'gray-3' }}
              lh="130"
            >
              {currentSlide + 1} / {slideCount}
            </Typography>
          </div>
          <div className={classes.leaguestarContainer}>
            <Typography
              fc={{ light: 'gray-3', dark: 'gray-3' }}
              lh="130"
              tt="uppercase"
            >
              Powered by
            </Typography>
            <LeaguestarLogo className={classes.leaguestarLogo} />
          </div>
        </div>
      </div>
    </div>
  )
}

function ChromeHeader({
  competitionId,
  slideTitle,
}: {
  competitionId: number
  slideTitle?: string
}) {
  const { data: competition } = useCompetition(competitionId)
  return (
    <>
      <Meta title={`${competition.name}`} />
      <div className={classes.header}>
        {competition.platform_logo && competition.platform_logo['main'] && (
          <Image
            src={competition.platform_logo['main']}
            alt={`${competition.name} platform logo`}
            className={classes.platformLogo}
          />
        )}
        {slideTitle?.trim() && (
          <div className={classes.slideTitle}>
            <Typography
              fs="xxxl"
              fw={700}
              fc={{ light: 'gray-0', dark: 'gray-0' }}
              lh="130"
            >
              {slideTitle}
            </Typography>
          </div>
        )}
        {competition.logo && (
          <AspectRatio ratio={1 / 1}>
            <Image
              src={competition.logo.main}
              alt={`${competition.name} logo`}
              className={classes.competitionLogo}
              fit="contain"
            />
          </AspectRatio>
        )}
      </div>
    </>
  )
}

function ChromeFooterUpdated({ competitionId }: { competitionId: number }) {
  const { data: competition } = useCompetition(competitionId)
  const last_updated = competition?.last_updated
  if (!last_updated) return <div />
  return (
    <div className={classes.updateWrapper}>
      <IconRefresh className={classes.refreshIcon} />
      <Typography
        fs="xl"
        fw={600}
        fc={{ light: 'gray-3', dark: 'gray-3' }}
        lh="130"
      >
        Updated:
      </Typography>
      <Typography
        fs="xl"
        fw={400}
        fc={{ light: 'gray-3', dark: 'gray-3' }}
        lh="130"
      >
        {formatDateString(last_updated)}
      </Typography>
    </div>
  )
}
